/**
 * Passenger syncEntryId helpers.
 *
 * The kiosk owns the UUID: one per passenger, minted on Continuar and reused by
 * the return leg (same passenger ⇒ same syncEntryId on both seats).
 */

/**
 * @returns {string} UUID v4
 */
export function createSyncEntryId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  // Non-secure fallback for browsers without crypto.randomUUID (kiosk shell has it).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Reuse the UUID already stored on the passenger (return leg); mint one only
 * for a passenger that is not in `selected` yet.
 *
 * @param {{ syncEntryId?: string|null }|null|undefined} passenger - from PassengerView
 * @param {{ syncEntryId?: string|null }|null|undefined} [existingPassenger] - match in `selected`
 * @returns {string}
 */
export function resolvePassengerSyncEntryId(passenger, existingPassenger) {
  const stored = passenger?.syncEntryId ?? existingPassenger?.syncEntryId
  if (stored != null && stored !== "") return String(stored)
  return createSyncEntryId()
}

/**
 * Find seat on a passenger by seatId + type (leg-aware).
 *
 * Round-trip seats often share the same Betterez seatId across legs, so the
 * lookup must key on seatId + leg, never seatId alone.
 *
 * @param {{ seats?: Array<{ seatId?: string|number, type?: string }> }|null|undefined} passenger
 * @param {{ seatId: string|number, type?: string }} seatRef
 * @returns {object|undefined}
 */
export function findPassengerSeat(passenger, seatRef) {
  if (!passenger?.seats || seatRef?.seatId == null) return undefined
  const targetId = String(seatRef.seatId)
  const targetType = seatRef.type === "return" ? "return" : "departure"
  return passenger.seats.find(
    (s) =>
      String(s.seatId) === targetId &&
      (s.type === "return" ? "return" : "departure") === targetType,
  )
}
